import { KeyValuePipe } from '@angular/common';
import { Component, effect, input, output, untracked } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ModelNameFlags } from '@era/locations-sdk';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { PaneComponent, YesNoIconComponent } from '@rocksoft/cdk';
import type { CategoryType } from '../../types/category.types';

export type ModelsSetPerSetting = Record<
  string,
  Record<Exclude<ModelNameFlags, 'None' | 'All'>, boolean> & {
    displayName: string;
    displayNameString?: string;
    categoryId?: string;
    categoryType?: CategoryType;
  }
>;

export type ConfigurationSetPerModelName = Record<Exclude<ModelNameFlags, 'None' | 'All'>, string[]>;
export interface MatrixOptions {
  settingsTableHeader: string;
  settingsFirstColumnHeader: string;
  settingsSecondColumnHeader?: string;
  allowMultiple: boolean;
  displayedModels: ModelNameFlags[];
}

@Component({
  selector: 'era-selection-matrix',
  templateUrl: './selection-matrix.component.html',
  styleUrls: ['./selection-matrix.component.scss'],
  imports: [PaneComponent, ReactiveFormsModule, FaIconComponent, YesNoIconComponent, KeyValuePipe],
})
export class SelectionMatrixComponent {
  public setting: ModelsSetPerSetting;
  public localModelsSetPerSetting: ModelsSetPerSetting;

  public readonly matrixOptions = input.required<MatrixOptions>();
  public readonly allowEdit = input(true);
  public readonly modelsSetPerSetting = input.required<ModelsSetPerSetting>();
  public readonly currentLevel = input.required<string>();
  public readonly settingSelectionChange = output<ModelsSetPerSetting>();

  public editing = false;
  public modelsSetPerSettingForm: UntypedFormGroup = new UntypedFormGroup({});

  constructor() {
    effect(() => {
      const options = this.matrixOptions();
      if (!options) return;
      if (this.localModelsSetPerSetting) {
        // Recreate the controls when the options change. Needed because of allowMultiple.
        this.matrixToFormGroup(options);
      }
    });

    effect(() => {
      const modelsSetPerSetting = this.modelsSetPerSetting();
      const options = untracked(() => this.matrixOptions());

      if (!modelsSetPerSetting) return;

      // Create a local copy, so we can use the original to revert changes when we cancel editing.
      this.resetConfigurationState(modelsSetPerSetting, options);
      this.matrixToFormGroup(options);
    });
  }

  private matrixToFormGroup(options: MatrixOptions) {
    this.modelsSetPerSettingForm.setControl('setting', new UntypedFormControl());
    if (options.allowMultiple) {
      // It should be possible to select multiple profiles for one model.
      for (const settingId in this.localModelsSetPerSetting) {
        this.modelsSetPerSettingForm.setControl(
          `${settingId}_AppleTV`,
          new UntypedFormControl(this.localModelsSetPerSetting[settingId].AppleTV)
        );
        this.modelsSetPerSettingForm.setControl(
          `${settingId}_iPad`,
          new UntypedFormControl(this.localModelsSetPerSetting[settingId].iPad)
        );
        this.modelsSetPerSettingForm.setControl(
          `${settingId}_iPhone`,
          new UntypedFormControl(this.localModelsSetPerSetting[settingId].iPhone)
        );
        this.modelsSetPerSettingForm.setControl(
          `${settingId}_iPod`,
          new UntypedFormControl(this.localModelsSetPerSetting[settingId].iPod)
        );
      }
    } else {
      // It should only be possible to select one profile per model.
      const defaultAppleTV = Object.keys(this.localModelsSetPerSetting).find(
        (key) => this.localModelsSetPerSetting[key].AppleTV
      );
      const defaultiPad = Object.keys(this.localModelsSetPerSetting).find(
        (key) => this.localModelsSetPerSetting[key].iPad
      );
      const defaultiPhone = Object.keys(this.localModelsSetPerSetting).find(
        (key) => this.localModelsSetPerSetting[key].iPhone
      );
      const defaultiPod = Object.keys(this.localModelsSetPerSetting).find(
        (key) => this.localModelsSetPerSetting[key].iPod
      );

      this.modelsSetPerSettingForm.setControl(`AppleTV`, new UntypedFormControl(defaultAppleTV));
      this.modelsSetPerSettingForm.setControl(`iPad`, new UntypedFormControl(defaultiPad));
      this.modelsSetPerSettingForm.setControl(`iPhone`, new UntypedFormControl(defaultiPhone));
      this.modelsSetPerSettingForm.setControl(`iPod`, new UntypedFormControl(defaultiPod));
    }
  }

  public edit() {
    this.editing = true;
  }
  public cancel() {
    // Reset the local state.
    this.editing = false;
    this.resetConfigurationState(this.modelsSetPerSetting(), this.matrixOptions());
  }

  public save() {
    this.editing = false;
    const savedModelsSetPerSetting: ModelsSetPerSetting = {};

    const options = this.matrixOptions();

    if (options.allowMultiple) {
      this.buildSavedModelsSetPerSettingForMultiple(savedModelsSetPerSetting);
    } else {
      this.buildSavedModelsSetPerSettingForSingle(savedModelsSetPerSetting);
    }

    this.settingSelectionChange.emit(savedModelsSetPerSetting);

    // Makes sure inherited configurations are shown again after they have been removed from the
    // matrix and no new configuration is added.
    this.resetConfigurationState(this.modelsSetPerSetting(), this.matrixOptions());
  }

  public add() {
    // Add to the local state
    const selectedConfiguration = this.modelsSetPerSettingForm.get(`setting`)?.value;
    this.localModelsSetPerSetting[selectedConfiguration.key] = selectedConfiguration.value;

    // Recreate the controls because there might not be one for the added profile
    this.matrixToFormGroup(this.matrixOptions());
  }

  public delete(row) {
    // Remove from the local state
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- row.key is guaranteed to be a valid key from the keyvalue pipe
    delete this.localModelsSetPerSetting[row.key];
  }

  private resetConfigurationState(modelsSetPerSetting: ModelsSetPerSetting, options: MatrixOptions) {
    this.localModelsSetPerSetting = {};

    if (options.allowMultiple) {
      // Multiple select mode: Add all configurations to the local state which have a link to a [Level]Configuration.
      for (const key in modelsSetPerSetting) {
        const item = modelsSetPerSetting[key];
        if (item.categoryId) {
          this.localModelsSetPerSetting[key] = item;
        }
      }
    } else {
      // Single select mode: For each device, only show the profile from the deepest level.
      const deepestProfilePerDevice = this.findDeepestProfilePerDevice();
      this.addFilteredProfilesToLocalState(deepestProfilePerDevice);
    }
  }

  /**
   * Gets the hierarchy level for a category type.
   * Brand (0) < Location (1) < Room/Kiosk (2)
   */
  private getCategoryLevel(categoryType: CategoryType): number {
    if (categoryType === 'Brand') return 0;
    if (categoryType === 'Location') return 1;
    if (categoryType === 'Room' || categoryType === 'Kiosk') return 2;
    return -1; // Invalid category type
  }

  /**
   * Finds the deepest profile for each device in single-select mode.
   * Only considers Brand, Location, Room, and Kiosk category types.
   */
  private findDeepestProfilePerDevice(): Map<string, { key: string; level: number }> {
    const devices = ['AppleTV', 'iPad', 'iPhone', 'iPod'] as const;
    const deepestProfilePerDevice = new Map<string, { key: string; level: number }>();
    const validCategoryTypes: CategoryType[] = ['Brand', 'Location', 'Room', 'Kiosk'];

    for (const device of devices) {
      const profilesForDevice: { key: string; level: number }[] = [];

      for (const key in this.modelsSetPerSetting()) {
        const item = this.modelsSetPerSetting()[key];
        if (item[device] && item.categoryId && item.categoryType && validCategoryTypes.includes(item.categoryType)) {
          const level = this.getCategoryLevel(item.categoryType);
          profilesForDevice.push({ key, level });
        }
      }

      if (profilesForDevice.length > 0) {
        const deepestProfile = profilesForDevice.reduce((prev, current) =>
          current.level > prev.level ? current : prev
        );
        deepestProfilePerDevice.set(device, deepestProfile);
      }
    }

    return deepestProfilePerDevice;
  }

  /**
   * Adds profiles to localModelsSetPerSetting, filtering device values so that
   * only devices where the profile is the deepest level are set to true.
   */
  private addFilteredProfilesToLocalState(deepestProfilePerDevice: Map<string, { key: string; level: number }>): void {
    const devices = ['AppleTV', 'iPad', 'iPhone', 'iPod'] as const;
    const profilesToInclude = new Set<string>();

    deepestProfilePerDevice.forEach((profile) => {
      profilesToInclude.add(profile.key);
    });

    for (const key of profilesToInclude) {
      const originalItem = this.modelsSetPerSetting()[key];
      const filteredItem = {
        ...originalItem,
        AppleTV: false,
        iPad: false,
        iPhone: false,
        iPod: false,
      };

      for (const device of devices) {
        const deepestForDevice = deepestProfilePerDevice.get(device);
        if (deepestForDevice?.key === key) {
          filteredItem[device] = originalItem[device];
        }
      }

      this.localModelsSetPerSetting[key] = filteredItem;
    }
  }

  /**
   * Builds the models set per setting for multiple settings.
   * Only adds the setting if at least one value differs from the original.
   * Settings configured at the current level are preserved even if unchanged.
   * @param savedModelsSetPerSetting The models set per setting to build.
   */
  private buildSavedModelsSetPerSettingForMultiple(savedModelsSetPerSetting: ModelsSetPerSetting): void {
    for (const settingId in this.localModelsSetPerSetting) {
      // Get the selected profile for each model type.
      const selectedAppleTv = this.modelsSetPerSettingForm.get(`${settingId}_AppleTV`)?.value;
      const selectedIpad = this.modelsSetPerSettingForm.get(`${settingId}_iPad`)?.value;
      const selectedIphone = this.modelsSetPerSettingForm.get(`${settingId}_iPhone`)?.value;
      const selectedIpod = this.modelsSetPerSettingForm.get(`${settingId}_iPod`)?.value;

      // Get the original values from localModelsSetPerSetting
      const originalSetting = this.modelsSetPerSetting()[settingId];
      const originalAppleTv = originalSetting?.AppleTV ?? false;
      const originalIpad = originalSetting?.iPad ?? false;
      const originalIphone = originalSetting?.iPhone ?? false;
      const originalIpod = originalSetting?.iPod ?? false;

      // Only add the setting if at least one value differs from the original
      const hasChanges =
        selectedAppleTv !== originalAppleTv ||
        selectedIpad !== originalIpad ||
        selectedIphone !== originalIphone ||
        selectedIpod !== originalIpod;

      // Check if this setting is configured at the current level
      const settingInModelsSet = this.modelsSetPerSetting()[settingId];
      const isConfiguredAtCurrentLevel =
        settingInModelsSet &&
        !!settingInModelsSet.categoryId &&
        !!settingInModelsSet.categoryType &&
        settingInModelsSet.categoryType === this.currentLevel();

      // If any value changed OR if the setting is configured at the current level,
      // include the setting with all its current values
      if (hasChanges || isConfiguredAtCurrentLevel) {
        savedModelsSetPerSetting[settingId] = {
          AppleTV: selectedAppleTv,
          iPad: selectedIpad,
          iPhone: selectedIphone,
          iPod: selectedIpod,
          displayName: this.modelsSetPerSetting()[settingId].displayName,
        };
      }
    }
  }

  /**
   * Builds the models set per setting for single-select mode.
   * Only adds the setting if the selection has changed from the original.
   * When a profile has any device selection change, the entire profile with all its device selections is included.
   * @param savedModelsSetPerSetting The models set per setting to build.
   */
  private buildSavedModelsSetPerSettingForSingle(savedModelsSetPerSetting: ModelsSetPerSetting): void {
    // Get the selected profile for each model type.
    const selectedAppleTv: string = this.modelsSetPerSettingForm.get('AppleTV')?.value;
    const selectediPad: string = this.modelsSetPerSettingForm.get('iPad')?.value;
    const selectediPhone: string = this.modelsSetPerSettingForm.get('iPhone')?.value;
    const selectediPod: string = this.modelsSetPerSettingForm.get('iPod')?.value;

    // Find the originally selected profile for each device type
    const originalAppleTv = Object.keys(this.modelsSetPerSetting()).find(
      (key) => this.modelsSetPerSetting()[key].AppleTV
    );
    const originaliPad = Object.keys(this.modelsSetPerSetting()).find((key) => this.modelsSetPerSetting()[key].iPad);
    const originaliPhone = Object.keys(this.modelsSetPerSetting()).find(
      (key) => this.modelsSetPerSetting()[key].iPhone
    );
    const originaliPod = Object.keys(this.modelsSetPerSetting()).find((key) => this.modelsSetPerSetting()[key].iPod);

    // Collect all profiles that are currently selected for any device
    // Only include profiles that exist in localModelsSetPerSetting (not deleted)
    const selectedProfiles = new Set<string>();
    if (selectedAppleTv && this.localModelsSetPerSetting[selectedAppleTv]) {
      selectedProfiles.add(selectedAppleTv);
    }
    if (selectediPad && this.localModelsSetPerSetting[selectediPad]) {
      selectedProfiles.add(selectediPad);
    }
    if (selectediPhone && this.localModelsSetPerSetting[selectediPhone]) {
      selectedProfiles.add(selectediPhone);
    }
    if (selectediPod && this.localModelsSetPerSetting[selectediPod]) {
      selectedProfiles.add(selectediPod);
    }

    // For each selected profile, check if any of its device selections have changed
    for (const profileId of selectedProfiles) {
      const modelsSetPerSetting = this.modelsSetPerSetting();
      if (!modelsSetPerSetting[profileId]) {
        continue;
      }

      // Check if any device selection for this profile has changed
      const hasAppleTvChanged = selectedAppleTv === profileId && selectedAppleTv !== originalAppleTv;
      const hasIpadChanged = selectediPad === profileId && selectediPad !== originaliPad;
      const hasIphoneChanged = selectediPhone === profileId && selectediPhone !== originaliPhone;
      const hasIpodChanged = selectediPod === profileId && selectediPod !== originaliPod;

      // Check if this profile is configured at the current level
      const profileInModelsSet = modelsSetPerSetting[profileId];
      const isConfiguredAtCurrentLevel =
        profileInModelsSet &&
        !!profileInModelsSet.categoryId &&
        !!profileInModelsSet.categoryType &&
        profileInModelsSet.categoryType === this.currentLevel();

      // If any device selection changed OR if the profile is configured at the current level,
      // include the entire profile with all its current device selections
      if (hasAppleTvChanged || hasIpadChanged || hasIphoneChanged || hasIpodChanged || isConfiguredAtCurrentLevel) {
        savedModelsSetPerSetting[profileId] = {
          AppleTV: selectedAppleTv === profileId,
          iPad: selectediPad === profileId,
          iPhone: selectediPhone === profileId,
          iPod: selectediPod === profileId,
          displayName: modelsSetPerSetting[profileId].displayName,
        };
      }
    }
  }
}
